import React, { useEffect, useState, useCallback } from 'react';
import api from '../lib/api';
import CornerPanel from '../components/CornerPanel';
import RefreshButton from '../components/RefreshButton';
import useVisibilityAwareInterval from '../hooks/useVisibilityAwareInterval';
import { useAuth } from '../lib/auth';
import { ShieldCheck, Bot, TrendingUp, TrendingDown, AlertTriangle, Zap, Eye, Plus, X, Play, Power, Sliders } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Iter 89 · Group 1 — Auto Trading Vault (Phase 2)
 *
 * Alpaca is the authoritative source of money-state; this page renders
 * a read-only snapshot pulled from /api/vault/*. Admin-only surface —
 * autonomous trading requires elevated authorization by design.
 *
 * Group 1 scope:
 *   * broker mode banner (NONE / PAPER / LIVE)
 *   * account snapshot (equity, cash, buying power, portfolio value)
 *   * live positions from Alpaca
 *   * open + recent-closed orders
 * Groups 2–15 will add market watch, signals, risk engine, execution,
 * autonomous mode toggles, etc. — the layout leaves room for those.
 */
const MODE_STYLE = {
  none:  { color: 'text-[var(--txt-muted)]', border: 'border-[var(--border-default)]', label: '● NO BROKER',                                       hint: 'Set ALPACA_API_KEY in backend/.env to activate' },
  paper: { color: 'text-hold',                border: 'border-hold',                    label: '◐ ALPACA PAPER · SIMULATED · NO REAL MONEY',        hint: 'Every order routes to Alpaca Paper — zero real capital at risk. Live trading is disabled in this build.' },
  live:  { color: 'text-sell',                border: 'border-sell',                    label: '◉ LIVE — REAL MONEY',                              hint: 'Live account — every order moves real capital' },
};

const fmtUSD = (n) => n == null ? '—' : `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtPct = (n) => n == null ? '—' : `${Number(n).toFixed(2)}%`;

export default function AutoTrading() {
  const { role } = useAuth();
  const [status, setStatus]       = useState(null);
  const [account, setAccount]     = useState(null);
  const [positions, setPositions] = useState([]);
  const [orders, setOrders]       = useState([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);
  const [last, setLast]           = useState(null);
  // Iter 90 · Group 2 — Watch Engine state
  const [watchEngine, setWatchEngine] = useState(null);
  const [watching, setWatching]       = useState([]);
  const [newSym, setNewSym]           = useState('');
  const [adding, setAdding]           = useState(false);
  // Iter 96 · Group 7 — Position Manager + P&L
  const [managed, setManaged]         = useState([]);
  const [posEngine, setPosEngine]     = useState(null);
  const [pnl, setPnl]                 = useState(null);
  const [posCfg, setPosCfg]           = useState(null);
  const [showPosCfg, setShowPosCfg]   = useState(false);
  // Iter 97 · Group 8 — Shadow Mode
  const [shadowMode, setShadowMode]         = useState('off');
  const [shadowEngine, setShadowEngine]     = useState(null);
  const [shadowDecisions, setShadowDecisions] = useState([]);
  const [shadowStats, setShadowStats]       = useState(null);
  const [modeChanging, setModeChanging]     = useState(false);
  // Iter 98 · Group 8 · Phase 15.5 — Shadow Validation Suite
  const [validation, setValidation]         = useState(null);
  const [validationWindow, setValidationWindow] = useState(168);   // hours
  // Iter 99 — version + horizon selectors
  const [validationVersion, setValidationVersion] = useState('v2_tuned');
  const [validationHorizon, setValidationHorizon] = useState('20m');
  const [availableVersions, setAvailableVersions] = useState(null);
  // Iter 100 — Replay Lab
  const [replayGates, setReplayGates]       = useState({ min_score: 80, min_rr: 2.0 });
  const [replayResult, setReplayResult]     = useState(null);
  const [replayPresets, setReplayPresets]   = useState(null);
  const [replayRunning, setReplayRunning]   = useState(false);

  const load = useCallback(async () => {
    if (role !== 'admin') return;
    setLoading(true);
    setError(null);
    try {
      const [s, a, p, o, w, mg, pn, sm, sd, ss, sv] = await Promise.allSettled([
        api.get('/vault/status'),
        api.get('/vault/account'),
        api.get('/vault/positions'),
        api.get('/vault/orders?status=all&limit=25'),
        api.get('/vault/watch'),
        api.get('/vault/positions/managed'),
        api.get('/vault/pnl'),
        api.get('/vault/shadow/mode'),
        api.get('/vault/shadow/decisions?limit=25&since_hours=24'),
        api.get('/vault/shadow/stats?since_hours=24'),
        api.get(`/vault/shadow/validation?since_hours=${validationWindow}&version=${validationVersion}&horizon=${validationHorizon}`),
      ]);
      setStatus(s.status   === 'fulfilled' ? s.value.data : null);
      setAccount(a.status  === 'fulfilled' ? a.value.data : null);
      setPositions(p.status === 'fulfilled' ? (p.value.data.positions || []) : []);
      setOrders(o.status    === 'fulfilled' ? (o.value.data.orders    || []) : []);
      if (w.status === 'fulfilled') {
        setWatchEngine(w.value.data.engine || null);
        setWatching(w.value.data.watching || []);
      }
      if (mg.status === 'fulfilled') {
        setManaged(mg.value.data.positions || []);
        setPosEngine(mg.value.data.engine || null);
      }
      if (pn.status === 'fulfilled') setPnl(pn.value.data);
      if (sm.status === 'fulfilled') {
        setShadowMode(sm.value.data.mode?.mode || 'off');
        setShadowEngine(sm.value.data.engine || null);
      }
      if (sd.status === 'fulfilled') setShadowDecisions(sd.value.data.rows || []);
      if (ss.status === 'fulfilled') setShadowStats(ss.value.data);
      if (sv.status === 'fulfilled') setValidation(sv.value.data);
      // If everything failed, surface the first message.
      if ([s, a, p, o, w].every((r) => r.status === 'rejected')) {
        setError(s.reason?.response?.data?.detail || 'Broker unavailable');
      }
      setLast(new Date().toISOString());
    } finally {
      setLoading(false);
    }
  }, [role, validationWindow, validationVersion, validationHorizon]);

  useEffect(() => { load(); }, [load]);
  useVisibilityAwareInterval(load, 20000, [load]);

  const addWatch = async () => {
    const sym = newSym.trim().toUpperCase();
    if (!sym || sym.length > 8) { toast.error('Enter a valid ticker'); return; }
    setAdding(true);
    try {
      await api.post('/vault/watch', { symbol: sym });
      toast.success(`Watching ${sym}`);
      setNewSym('');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Add failed');
    } finally { setAdding(false); }
  };

  const removeWatch = async (sym) => {
    try {
      await api.delete(`/vault/watch/${sym}`);
      toast.success(`Removed ${sym}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Remove failed');
    }
  };

  // Iter 93 · Group 5 · Phase 10 — sync watch list to top Discovery candidates
  const [syncing, setSyncing] = useState(false);
  const syncUniverse = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const { data } = await api.post('/vault/universe/sync', {});
      const added   = (data.added || []).length;
      const removed = (data.removed || []).length;
      toast.success(`Universe synced — +${added} / -${removed}`, {
        description: (data.candidates || []).slice(0, 5).map((c) => c.symbol).join(', '),
      });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Sync failed');
    } finally { setSyncing(false); }
  };

  // Iter 94 · Group 6 · Phase 11+12 — Risk Engine + Order Management
  const [riskCfg, setRiskCfg]     = useState(null);
  const [killSw, setKillSw]       = useState({ active: false });
  const [executing, setExecuting] = useState({});   // symbol → bool
  const [showCfg, setShowCfg]     = useState(false);

  const loadRisk = useCallback(async () => {
    try {
      const { data } = await api.get('/vault/risk/config');
      setRiskCfg(data.config);
      setKillSw(data.kill_switch || { active: false });
    } catch {}
    try {
      const { data } = await api.get('/vault/position-config');
      setPosCfg(data.config);
    } catch {}
    try {
      const { data } = await api.get('/vault/shadow/validation/versions');
      setAvailableVersions(data);
    } catch {}
    try {
      const { data } = await api.get('/vault/shadow/replay/presets');
      setReplayPresets(data);
    } catch {}
  }, []);
  useEffect(() => { if (role === 'admin') loadRisk(); }, [role, loadRisk]);

  const toggleKill = async () => {
    try {
      const next = !killSw.active;
      const { data } = await api.post('/vault/risk/kill-switch',
        { active: next, reason: next ? 'manual toggle' : null });
      setKillSw(data);
      toast[next ? 'error' : 'success'](
        next ? 'KILL SWITCH ARMED — no new orders will submit' : 'Kill switch disarmed');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Kill switch toggle failed');
    }
  };

  const updateCfg = async (patch) => {
    try {
      const { data } = await api.put('/vault/risk/config', patch);
      setRiskCfg(data.config);
      toast.success('Risk config updated');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Update failed');
    }
  };

  const executeOrder = async (sym) => {
    if (executing[sym]) return;
    if (!window.confirm(`Submit bracket order for ${sym}?\n\nRuns full confluence → risk → Alpaca pipeline.`)) return;
    setExecuting((m) => ({ ...m, [sym]: true }));
    try {
      const { data } = await api.post(`/vault/order/execute/${sym}`);
      if (data.status === 'submitted') {
        toast.success(`${sym} submitted — ${data.risk?.qty}× @ ~$${data.risk?.notional?.toFixed?.(0)}`,
          { description: `client_id ${data.client_order_id?.slice?.(0, 20)}…` });
      } else {
        toast.error(`${sym} rejected: ${data.error || data.status}`,
          { description: (data.risk?.vetoes || []).slice(0, 2).join(' · ') });
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Execute failed');
    } finally {
      setExecuting((m) => ({ ...m, [sym]: false }));
    }
  };

  const closePosition = async (sym) => {
    if (!window.confirm(`Close position ${sym} at market?`)) return;
    try {
      await api.post(`/vault/position/close/${sym}`);
      toast.success(`Closed ${sym}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Close failed');
    }
  };

  // Group 7 · position config PUT + force tick
  const updatePosCfg = async (patch) => {
    try {
      const { data } = await api.put('/vault/position-config', patch);
      setPosCfg(data.config);
      toast.success('Position config updated');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Update failed');
    }
  };

  const forceTick = async () => {
    try {
      const { data } = await api.post('/vault/position-engine/tick');
      toast.success(`Reconciled — alpaca=${data.alpaca_ct} mongo=${data.mongo_open_ct} actions=${(data.actions||[]).length}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Tick failed');
    }
  };

  // Iter 97 · Group 8 — Shadow Mode handlers
  const changeShadowMode = async (next) => {
    if (modeChanging || next === shadowMode) return;
    setModeChanging(true);
    try {
      await api.put('/vault/shadow/mode', { mode: next, reason: 'ui toggle' });
      toast.success(next === 'shadow'
        ? 'Shadow Mode ARMED — bot will log hypothetical trades every 60s'
        : 'Shadow Mode disabled');
      setShadowMode(next);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Mode change failed');
    } finally { setModeChanging(false); }
  };

  const forceShadowTick = async () => {
    try {
      const { data } = await api.post('/vault/shadow/tick');
      if (data.skipped) {
        toast.info(`Skipped — mode=${data.mode}`);
      } else {
        toast.success(`Shadow tick — ${data.symbols_evaluated} evaluated · ${data.decisions_written} logged`);
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Shadow tick failed');
    }
  };

  // Iter 100 — Replay Lab handlers
  const runReplay = async () => {
    if (replayRunning) return;
    setReplayRunning(true);
    try {
      const { data } = await api.post('/vault/shadow/replay', {
        gates: replayGates,
        since_hours: validationWindow,
        version: validationVersion,
        horizon: validationHorizon,
      });
      setReplayResult(data);
      toast.success(`Replay done — ${data.counts.counterfactual_approved} approved · Δ ${data.approval_rate_delta_pct}%`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Replay failed');
    } finally { setReplayRunning(false); }
  };

  const loadPreset = (key) => {
    const p = replayPresets?.presets?.[key];
    if (!p) return;
    setReplayGates({ ...p.gates });
    toast.info(`Loaded preset: ${p.label}`);
  };

  if (role && role !== 'admin') {
    return (
      <div className="p-8 text-center">
        <ShieldCheck className="mx-auto mb-4 text-sell" size={32} />
        <div className="text-sell text-lg tracking-widest uppercase font-bold">Admin required</div>
        <div className="text-[var(--txt-secondary)] text-sm mt-2">Auto Trading is restricted to admin operators.</div>
      </div>
    );
  }

  const mode = (status?.mode) || 'none';
  const ms = MODE_STYLE[mode] || MODE_STYLE.none;

  return (
    <div className="p-4 space-y-3" data-testid="auto-trading-page">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <div className="text-[10px] text-[var(--txt-muted)] uppercase tracking-widest">// ATDE.autotrader.v1 · Group 1</div>
          <div className="text-cyan-sys text-lg font-bold tracking-widest uppercase mt-0.5 flex items-center gap-2">
            <Bot size={18} /> AUTO TRADING VAULT
          </div>
        </div>
        <RefreshButton loading={loading} last={last} onClick={load} data-testid="vault-refresh" />
      </div>

      {/* Mode banner */}
      <div data-testid="vault-mode-banner"
           className={`border ${ms.border} bg-black/40 px-3 py-2 flex flex-wrap items-center justify-between gap-2`}>
        <div className={`font-bold tracking-[0.3em] uppercase text-[12px] ${ms.color}`}>
          {ms.label}
        </div>
        <div className="text-[10px] text-[var(--txt-secondary)]">{ms.hint}</div>
        {status?.health?.market_open != null && (
          <div className={`text-[10px] tracking-widest uppercase ${status.health.market_open ? 'text-buy' : 'text-hold'}`}>
            market {status.health.market_open ? '● open' : '○ closed'}
          </div>
        )}
      </div>

      {/* PAPER-ONLY safety banner — always visible in paper mode */}
      {mode === 'paper' && (
        <div data-testid="paper-only-banner"
             className="border-2 border-hold bg-hold/10 px-3 py-2 flex flex-wrap items-center gap-3">
          <div className="font-bold tracking-[0.3em] uppercase text-[11px] text-hold">
            ⚠ PAPER TRADING ONLY · NO REAL MONEY
          </div>
          <div className="text-[10px] text-[var(--txt-secondary)] flex-1">
            Live trading is intentionally disabled in this build. Every order routes to <span className="text-hold font-bold">Alpaca Paper</span> — a simulated broker sandbox. Kill Switch remains the highest-priority veto over every AI signal, strategy prompt, and manual execute.
          </div>
        </div>
      )}

      {/* Iter 94 · Group 6 — Kill Switch + Risk Config bar */}
      <div data-testid="risk-bar"
           className={`border ${killSw.active ? 'border-sell' : 'border-[var(--border-default)]'} bg-black/40 px-3 py-2 flex flex-wrap items-center justify-between gap-3`}>
        <button
          onClick={toggleKill}
          data-testid="kill-switch-btn"
          title={killSw.active ? 'Disarm kill switch' : 'Arm kill switch — blocks every new order'}
          className={`inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest px-3 py-1.5 border transition-colors ${killSw.active ? 'bg-sell text-black border-sell hover:bg-sell/80' : 'border-sell text-sell hover:bg-sell hover:text-black'}`}
        >
          <Power size={12} /> {killSw.active ? '● KILL ARMED — click to disarm' : '○ arm kill switch'}
        </button>
        <div className="flex items-center gap-3 text-[10px] font-mono text-[var(--txt-secondary)]" data-testid="risk-config-inline">
          {riskCfg && (
            <>
              <span title="Percent of equity risked per trade">risk <span className="text-cyan-sys">{riskCfg.risk_pct}%</span></span>
              <span title="Max concurrent open positions">max pos <span className="text-cyan-sys">{riskCfg.max_open_positions}</span></span>
              <span title="Max trades per day">daily trades <span className="text-cyan-sys">{riskCfg.max_daily_trades}</span></span>
              <span title="Max daily loss as % of equity">loss cap <span className="text-cyan-sys">{riskCfg.max_daily_loss_pct}%</span></span>
            </>
          )}
          <button onClick={() => setShowCfg((v) => !v)}
                  data-testid="risk-config-toggle-btn"
                  className="text-hold hover:text-cyan-sys inline-flex items-center gap-1 border border-[var(--border-default)] px-2 py-1 uppercase tracking-widest">
            <Sliders size={11} /> {showCfg ? 'hide' : 'edit'}
          </button>
        </div>
      </div>

      {/* Risk Config editor — collapsible */}
      {showCfg && riskCfg && (
        <div data-testid="risk-config-editor"
             className="border border-[var(--border-default)] bg-black/40 px-3 py-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px] font-mono">
          {[
            ['risk_pct',                     'Risk % / trade',        0.1, 5],
            ['max_open_positions',           'Max open positions',    1,   20],
            ['max_daily_trades',             'Max daily trades',      1,   100],
            ['max_daily_loss_pct',           'Daily loss cap %',      0.5, 20],
            ['max_exposure_pct_per_symbol',  'Per-symbol exposure %', 5,   100],
            ['max_total_exposure_pct',       'Total exposure %',      10,  100],
            ['cooldown_seconds',             'Per-symbol cooldown s', 5,   3600],
            ['min_notional_usd',             'Min notional $',        0.1, 100],
          ].map(([k, label, min, max]) => (
            <label key={k} className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-widest text-[var(--txt-muted)]">{label}</span>
              <input
                type="number"
                step="0.1"
                min={min}
                max={max}
                data-testid={`risk-cfg-${k}`}
                value={riskCfg[k]}
                onChange={(e) => setRiskCfg((c) => ({ ...c, [k]: Number(e.target.value) }))}
                onBlur={(e) => {
                  const next = Number(e.target.value);
                  // Skip no-op PUTs — avoids toast spam when the operator
                  // tabs through fields without changing them.
                  if (next === riskCfg[k]) return;
                  updateCfg({ [k]: next });
                }}
                className="bg-black border border-[var(--border-default)] focus:border-cyan-sys px-2 py-1 text-cyan-sys outline-none"
              />
            </label>
          ))}
        </div>
      )}

      {error && (
        <div data-testid="vault-error" className="border border-sell/60 bg-sell/10 text-sell px-3 py-2 text-[11px] flex items-center gap-2">
          <AlertTriangle size={12} /> {error}
        </div>
      )}

      {/* Account grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard testId="vault-stat-equity"       label="Equity"          value={fmtUSD(account?.equity)}           color="text-cyan-sys" />
        <StatCard testId="vault-stat-cash"         label="Cash"            value={fmtUSD(account?.cash)}             color="text-buy" />
        <StatCard testId="vault-stat-bp"           label="Buying Power"    value={fmtUSD(account?.buying_power)}     color="text-hold" />
        <StatCard testId="vault-stat-pv"           label="Portfolio Value" value={fmtUSD(account?.portfolio_value)}  color="text-cyan-sys" />
      </div>

      {/* Iter 96 · Group 7 · Phase 14 — P&L Dashboard */}
      {pnl && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="pnl-dashboard">
          <StatCard testId="pnl-realized-today" label="Realized today"
                    value={fmtUSD(pnl.realized_today)}
                    color={pnl.realized_today > 0 ? 'text-buy' : pnl.realized_today < 0 ? 'text-sell' : 'text-[var(--txt-secondary)]'} />
          <StatCard testId="pnl-unrealized" label="Unrealized"
                    value={fmtUSD(pnl.unrealized)}
                    color={pnl.unrealized > 0 ? 'text-buy' : pnl.unrealized < 0 ? 'text-sell' : 'text-[var(--txt-secondary)]'} />
          <StatCard testId="pnl-win-rate" label="Win Rate"
                    value={pnl.history.trades > 0 ? `${pnl.history.win_rate}%` : '—'}
                    color="text-hold" />
          <StatCard testId="pnl-avg-r" label="Avg R"
                    value={pnl.history.trades > 0 ? pnl.history.avg_r.toFixed(2) : '—'}
                    color={pnl.history.avg_r > 0 ? 'text-buy' : 'text-sell'} />
          <StatCard testId="pnl-best-r" label="Best R"
                    value={pnl.history.trades > 0 ? pnl.history.best_r.toFixed(2) : '—'}
                    color="text-buy" />
          <StatCard testId="pnl-worst-r" label="Worst R"
                    value={pnl.history.trades > 0 ? pnl.history.worst_r.toFixed(2) : '—'}
                    color="text-sell" />
        </div>
      )}

      {/* Positions */}
      <CornerPanel title={`Broker Positions // ${positions.length}`} testId="vault-positions-panel">
        <div className="mobile-scroll" data-sticky-first="true">
          <table className="w-full text-[12px] font-mono min-w-[720px]" data-testid="vault-positions-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-[var(--txt-muted)] border-b border-[var(--border-default)]">
                <th className="text-left py-2 px-2">symbol</th>
                <th className="text-left py-2 px-2">side</th>
                <th className="text-right py-2 px-2">qty</th>
                <th className="text-right py-2 px-2">avg entry</th>
                <th className="text-right py-2 px-2">last</th>
                <th className="text-right py-2 px-2">market val</th>
                <th className="text-right py-2 px-2">unrealized</th>
                <th className="text-right py-2 px-2">actions</th>
              </tr>
            </thead>
            <tbody>
              {positions.length === 0 && (
                <tr><td colSpan={8} className="py-6 text-center text-[var(--txt-muted)]">
                  {mode === 'none' ? '// broker not configured — set ALPACA_API_KEY to activate' : '// no open positions'}
                </td></tr>
              )}
              {positions.map((p) => {
                const pnlPos = (p.unrealized_pl || 0) >= 0;
                return (
                  <tr key={p.symbol} className="border-b border-[var(--border-default)]" data-testid={`vault-pos-${p.symbol}`}>
                    <td className="py-2 px-2 font-bold text-cyan-sys">{p.symbol}</td>
                    <td className="py-2 px-2 text-[10px] uppercase tracking-widest">
                      {p.side === 'long' ? <span className="text-buy inline-flex items-center gap-1"><TrendingUp size={11}/> LONG</span>
                                          : <span className="text-sell inline-flex items-center gap-1"><TrendingDown size={11}/> SHORT</span>}
                    </td>
                    <td className="py-2 px-2 text-right">{p.qty}</td>
                    <td className="py-2 px-2 text-right">{fmtUSD(p.entry_price)}</td>
                    <td className="py-2 px-2 text-right">{fmtUSD(p.current_price)}</td>
                    <td className="py-2 px-2 text-right">{fmtUSD(p.market_value)}</td>
                    <td className={`py-2 px-2 text-right ${pnlPos ? 'text-buy' : 'text-sell'}`}>
                      {fmtUSD(p.unrealized_pl)} <span className="text-[9px] opacity-70">({fmtPct(p.unrealized_pl_pct)})</span>
                    </td>
                    <td className="py-2 px-2 text-right">
                      <button onClick={() => closePosition(p.symbol)}
                              data-testid={`vault-pos-close-${p.symbol}`}
                              className="border border-sell/60 text-sell hover:bg-sell hover:text-black px-2 py-0.5 text-[10px] uppercase tracking-widest inline-flex items-center gap-1"
                              title="Close position at market">
                        <X size={10} /> close
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CornerPanel>

      {/* Orders */}
      <CornerPanel title={`Broker Orders // ${orders.length}`} testId="vault-orders-panel">
        <div className="mobile-scroll" data-sticky-first="true">
          <table className="w-full text-[12px] font-mono min-w-[720px]" data-testid="vault-orders-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-[var(--txt-muted)] border-b border-[var(--border-default)]">
                <th className="text-left py-2 px-2">symbol</th>
                <th className="text-left py-2 px-2">side</th>
                <th className="text-left py-2 px-2">type</th>
                <th className="text-right py-2 px-2">qty</th>
                <th className="text-right py-2 px-2">filled</th>
                <th className="text-right py-2 px-2">avg fill</th>
                <th className="text-left py-2 px-2">status</th>
                <th className="text-left py-2 px-2">submitted</th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 && (
                <tr><td colSpan={8} className="py-6 text-center text-[var(--txt-muted)]">
                  {mode === 'none' ? '// broker not configured' : '// no recent orders'}
                </td></tr>
              )}
              {orders.map((o) => {
                const filled = o.status === 'filled';
                const cancelled = ['canceled', 'expired', 'rejected'].includes(o.status);
                return (
                  <tr key={o.id} className="border-b border-[var(--border-default)]" data-testid={`vault-order-${o.id}`}>
                    <td className="py-2 px-2 font-bold text-cyan-sys">{o.symbol}</td>
                    <td className={`py-2 px-2 text-[10px] uppercase tracking-widest ${o.side === 'buy' ? 'text-buy' : 'text-sell'}`}>{o.side}</td>
                    <td className="py-2 px-2 text-[10px] uppercase tracking-widest text-[var(--txt-secondary)]">{o.order_type}</td>
                    <td className="py-2 px-2 text-right">{o.qty}</td>
                    <td className="py-2 px-2 text-right">{o.filled_qty}</td>
                    <td className="py-2 px-2 text-right">{o.filled_avg_price != null ? fmtUSD(o.filled_avg_price) : '—'}</td>
                    <td className={`py-2 px-2 text-[10px] uppercase tracking-widest ${filled ? 'text-buy' : cancelled ? 'text-sell' : 'text-hold'}`}>
                      {o.status}
                    </td>
                    <td className="py-2 px-2 text-[10px] text-[var(--txt-muted)]">
                      {o.submitted_at ? new Date(o.submitted_at).toLocaleString() : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CornerPanel>

      {/* Iter 90 · Group 2 — Market Watch Engine */}
      <CornerPanel
        title={`Market Watch Engine // ${watching.length}`}
        testId="watch-panel"
        right={
          <div className="flex items-center gap-2 text-[10px] font-mono">
            <span className={`uppercase tracking-widest ${watchEngine?.running ? 'text-buy' : 'text-sell'}`}
                  data-testid="watch-engine-status">
              {watchEngine?.running ? '● live' : '○ offline'}
            </span>
            {watchEngine?.last_tick_age_s != null && (
              <span className="text-[var(--txt-muted)]">
                tick {Math.round(watchEngine.last_tick_age_s)}s ago
              </span>
            )}
          </div>
        }
      >
        <div className="flex items-center gap-2 mb-3" data-testid="watch-add-row">
          <Eye size={12} className="text-[var(--txt-muted)]" />
          <input
            data-testid="watch-add-input"
            value={newSym}
            onChange={(e) => setNewSym(e.target.value.toUpperCase())}
            onKeyDown={(e) => { if (e.key === 'Enter') addWatch(); }}
            placeholder="ticker (e.g. AAPL)"
            maxLength={8}
            className="flex-1 max-w-[200px] bg-black border border-[var(--border-default)] focus:border-cyan-sys px-2 py-1.5 font-mono text-[12px] text-cyan-sys outline-none uppercase"
          />
          <button
            onClick={addWatch}
            disabled={adding}
            data-testid="watch-add-btn"
            className="bg-cyan-sys/10 border border-cyan-sys text-cyan-sys hover:bg-cyan-sys hover:text-black font-mono text-[11px] uppercase tracking-widest px-3 py-1.5 transition-colors inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            <Plus size={12} /> {adding ? 'watching…' : 'watch'}
          </button>
          <button
            onClick={syncUniverse}
            disabled={syncing}
            data-testid="watch-sync-universe-btn"
            title="Auto-sync watch list to top Discovery candidates (Group 5)"
            className="bg-hold/10 border border-hold text-hold hover:bg-hold hover:text-black font-mono text-[11px] uppercase tracking-widest px-3 py-1.5 transition-colors inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            <Zap size={12} /> {syncing ? 'syncing…' : 'sync from discovery'}
          </button>
        </div>
        <div className="mobile-scroll" data-sticky-first="true">
          <table className="w-full text-[12px] font-mono min-w-[1240px]" data-testid="watch-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-[var(--txt-muted)] border-b border-[var(--border-default)]">
                <th className="text-left  py-2 px-2">symbol</th>
                <th className="text-right py-2 px-2">last</th>
                <th className="text-right py-2 px-2">ema5/13</th>
                <th className="text-right py-2 px-2">atr%</th>
                <th className="text-left  py-2 px-2">regime</th>
                <th className="text-center py-2 px-2" title="Group 3 signal — EMA5/13 + 0.5×ATR (advisory only, Risk Engine veto in Group 6)">proposal</th>
                <th className="text-center py-2 px-2" title="Group 4 · Confluence Gate — fuses signal + regime + volatility + volume + Discovery + freshness into a single decision">verdict</th>
                <th className="text-right py-2 px-2">entry</th>
                <th className="text-right py-2 px-2">stop</th>
                <th className="text-right py-2 px-2">target</th>
                <th className="text-right py-2 px-2">r/r</th>
                <th className="text-left  py-2 px-2">freshness</th>
                <th className="text-right py-2 px-2">actions</th>
              </tr>
            </thead>
            <tbody>
              {watching.length === 0 && (
                <tr><td colSpan={13} className="py-6 text-center text-[var(--txt-muted)]">
                  // add a ticker above — engine will tick every {watchEngine?.interval_open_s ?? 30}s while market is open
                </td></tr>
              )}
              {watching.map((w) => {
                const ind = w.indicators || {};
                const reg = w.regime || {};
                const pr  = w.proposal || {};
                const psig = pr.signal || 'NO_TRADE';
                const psigC = psig === 'BUY' ? 'text-buy' : psig === 'SELL' ? 'text-sell' : 'text-[var(--txt-muted)]';
                const fresh = w.freshness || 'unknown';
                const freshC = fresh === 'fresh' ? 'text-buy' : fresh === 'delayed' ? 'text-hold' : 'text-sell';
                const vetoTip = (pr.vetoes && pr.vetoes.length) ? pr.vetoes.join(' · ')
                              : (pr.reasons && pr.reasons.length) ? pr.reasons.join(' · ')
                              : '';
                return (
                  <tr key={w.symbol} className="border-b border-[var(--border-default)]" data-testid={`watch-row-${w.symbol}`}>
                    <td className="py-2 px-2 font-bold text-cyan-sys">{w.symbol}</td>
                    <td className="py-2 px-2 text-right">{w.ok && w.last_price != null ? Number(w.last_price).toFixed(2) : '—'}</td>
                    <td className="py-2 px-2 text-right text-[10px]" title={`EMA gap ${pr.ema_gap != null ? pr.ema_gap.toFixed(2)+'%' : '—'}`}>
                      {pr.ema_fast != null ? Number(pr.ema_fast).toFixed(2) : '—'} / {pr.ema_slow != null ? Number(pr.ema_slow).toFixed(2) : '—'}
                    </td>
                    <td className="py-2 px-2 text-right">{ind.atr_pct != null ? Number(ind.atr_pct).toFixed(2) + '%' : '—'}</td>
                    <td className="py-2 px-2 text-[10px] uppercase tracking-widest text-[var(--txt-secondary)]">
                      {reg.trend || '—'}{reg.volatility ? ' / ' + reg.volatility : ''}
                    </td>
                    <td className={`py-2 px-2 text-center font-bold ${psigC}`} data-testid={`watch-proposal-${w.symbol}`} title={vetoTip}>
                      {psig}
                    </td>
                    {(() => {
                      const cg = w.confluence_gate || {};
                      const verdict = cg.verdict || 'NO_TRADE';
                      const vC = verdict === 'TRADE_APPROVED' ? 'text-buy border-buy' : 'text-sell border-sell/60';
                      const label = verdict === 'TRADE_APPROVED' ? '✔ APPROVED' : '✖ NO TRADE';
                      const gates = cg.gates || {};
                      const gateLines = Object.entries(gates).map(([k, v]) => `${v.ok ? '✓' : '✗'} ${k}: ${v.reason}`).join('\n');
                      const reasonLine = (cg.reasons || []).join(' · ');
                      const tip = [reasonLine, gateLines].filter(Boolean).join('\n\n');
                      return (
                        <td className="py-2 px-2 text-center" data-testid={`watch-verdict-${w.symbol}`} title={tip}>
                          <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 border ${vC}`}>
                            {label} {cg.score != null && <span className="opacity-70 font-mono">{cg.score}</span>}
                          </span>
                        </td>
                      );
                    })()}
                    <td className="py-2 px-2 text-right">{pr.entry != null ? Number(pr.entry).toFixed(2) : '—'}</td>
                    <td className="py-2 px-2 text-right text-sell">{pr.stop  != null ? Number(pr.stop).toFixed(2)  : '—'}</td>
                    <td className="py-2 px-2 text-right text-buy">{pr.target!= null ? Number(pr.target).toFixed(2): '—'}</td>
                    <td className="py-2 px-2 text-right">{pr.r_r != null ? pr.r_r.toFixed(2) : '—'}</td>
                    <td className={`py-2 px-2 text-[10px] uppercase tracking-widest ${freshC}`} data-testid={`watch-fresh-${w.symbol}`}
                        title={`${w.age_s ?? '?'}s since last update`}>
                      ● {fresh}
                    </td>
                    <td className="py-2 px-2 text-right">
                      {(() => {
                        const approved = (w.confluence_gate?.verdict) === 'TRADE_APPROVED';
                        return (
                          <div className="inline-flex items-center gap-2">
                            {approved && (
                              <button onClick={() => executeOrder(w.symbol)}
                                      disabled={!!executing[w.symbol] || killSw.active}
                                      data-testid={`watch-execute-${w.symbol}`}
                                      className="border border-buy text-buy hover:bg-buy hover:text-black px-2 py-0.5 text-[10px] uppercase tracking-widest inline-flex items-center gap-1 disabled:opacity-40"
                                      title={killSw.active ? 'Kill switch armed' : 'Execute bracket order via risk engine'}>
                                <Play size={10} /> {executing[w.symbol] ? '…' : 'exec'}
                              </button>
                            )}
                            <button onClick={() => removeWatch(w.symbol)}
                                    data-testid={`watch-remove-${w.symbol}`}
                                    className="text-[var(--txt-muted)] hover:text-sell inline-flex items-center gap-0.5"
                                    title="remove from watch list">
                              <X size={12} />
                            </button>
                          </div>
                        );
                      })()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CornerPanel>

      {/* Iter 97 · Group 8 · Phase 15 — Shadow Mode */}
      <CornerPanel
        title={`Shadow Mode · ${shadowMode === 'shadow' ? 'ARMED · logging' : 'off'}`}
        testId="shadow-panel"
        right={
          <div className="flex items-center gap-2 text-[10px] font-mono">
            <span className={`uppercase tracking-widest ${shadowEngine?.running ? 'text-buy' : 'text-sell'}`}
                  data-testid="shadow-engine-status">
              {shadowEngine?.running ? `● live · ${shadowEngine.cycles} cycles` : '○ offline'}
            </span>
            {shadowEngine?.last_error && (
              <span className="text-sell" title={shadowEngine.last_error}>⚠ err</span>
            )}
            <button onClick={forceShadowTick}
                    data-testid="shadow-tick-btn"
                    className="text-hold hover:text-cyan-sys border border-[var(--border-default)] px-2 py-0.5 uppercase tracking-widest">
              tick now
            </button>
          </div>
        }
      >
        <div className="flex flex-wrap items-center gap-3 mb-3" data-testid="shadow-mode-row">
          <span className="text-[10px] uppercase tracking-widest text-[var(--txt-muted)]">Operating mode</span>
          {['off', 'shadow'].map((m) => (
            <button
              key={m}
              onClick={() => changeShadowMode(m)}
              disabled={modeChanging}
              data-testid={`shadow-mode-${m}-btn`}
              className={`font-mono text-[11px] uppercase tracking-widest px-3 py-1.5 border transition-colors disabled:opacity-50 ${shadowMode === m ? (m === 'shadow' ? 'bg-hold text-black border-hold' : 'bg-[var(--txt-muted)]/20 border-[var(--txt-muted)] text-[var(--txt-secondary)]') : 'border-[var(--border-default)] text-[var(--txt-secondary)] hover:border-cyan-sys hover:text-cyan-sys'}`}
            >
              {shadowMode === m ? '●' : '○'} {m}
            </button>
          ))}
          <span className="text-[10px] text-[var(--txt-secondary)] flex-1">
            Shadow Mode records every decision the bot WOULD make. Nothing routes to Alpaca. Kill Switch still blocks logging into an SHADOW_KILLED status.
          </span>
        </div>

        {/* Iter 98/99 · Group 8 · Phase 15.5 — Shadow Validation Suite */}
        {validation && <ShadowValidationCard
          data={validation}
          window={validationWindow}
          onWindowChange={setValidationWindow}
          version={validationVersion}
          onVersionChange={setValidationVersion}
          horizon={validationHorizon}
          onHorizonChange={setValidationHorizon}
          availableVersions={availableVersions}
        />}

        {/* Iter 100 — Shadow Replay Lab (read-only counterfactual) */}
        <ShadowReplayLab
          gates={replayGates}
          onGatesChange={setReplayGates}
          onRun={runReplay}
          onLoadPreset={loadPreset}
          running={replayRunning}
          result={replayResult}
          presets={replayPresets}
        />

        {shadowStats && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-3 text-[11px] font-mono" data-testid="shadow-stats">
            <MiniStat testId="shadow-stat-total"        label="24h decisions" value={shadowStats.total}                color="text-cyan-sys" />
            <MiniStat testId="shadow-stat-approved"     label="approved"      value={`${shadowStats.approved} (${shadowStats.approval_rate_pct}%)`} color="text-buy" />
            <MiniStat testId="shadow-stat-rejected"     label="rejected"      value={shadowStats.rejected}             color="text-sell" />
            <MiniStat testId="shadow-stat-killed"       label="killed"        value={shadowStats.killed}               color="text-hold" />
            <MiniStat testId="shadow-stat-hit-rate"     label="hit rate"      value={`${shadowStats.hit_rate_pct}%`}   color="text-buy" />
            <MiniStat testId="shadow-stat-notional"     label="hyp. notional" value={fmtUSD(shadowStats.hypothetical_notional_total)} color="text-hold" />
          </div>
        )}

        {shadowStats?.top_vetoes?.length > 0 && (
          <div className="mb-3 text-[10px] font-mono text-[var(--txt-secondary)]" data-testid="shadow-veto-distribution">
            <span className="uppercase tracking-widest text-[var(--txt-muted)]">Top vetoes: </span>
            {shadowStats.top_vetoes.slice(0, 6).map(([k, v]) => (
              <span key={k} className="inline-block mr-2">
                <span className="text-sell">{k}</span> <span className="opacity-70">×{v}</span>
              </span>
            ))}
          </div>
        )}

        <div className="mobile-scroll" data-sticky-first="true">
          <table className="w-full text-[12px] font-mono min-w-[1100px]" data-testid="shadow-decisions-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-[var(--txt-muted)] border-b border-[var(--border-default)]">
                <th className="text-left  py-2 px-2">time</th>
                <th className="text-left  py-2 px-2">symbol</th>
                <th className="text-left  py-2 px-2">verdict</th>
                <th className="text-right py-2 px-2">qty</th>
                <th className="text-right py-2 px-2">entry</th>
                <th className="text-right py-2 px-2">stop</th>
                <th className="text-right py-2 px-2">target</th>
                <th className="text-right py-2 px-2">R:R</th>
                <th className="text-right py-2 px-2">notional</th>
                <th className="text-left  py-2 px-2">outcome</th>
                <th className="text-left  py-2 px-2">vetoes</th>
              </tr>
            </thead>
            <tbody>
              {shadowDecisions.length === 0 && (
                <tr><td colSpan={11} className="py-6 text-center text-[var(--txt-muted)]">
                  // {shadowMode === 'shadow' ? 'waiting for first cycle — tick fires every 60s' : 'switch mode to SHADOW to start recording hypothetical trades'}
                </td></tr>
              )}
              {shadowDecisions.map((d) => {
                const hyp = d.hypothetical || {};
                const sv  = d.shadow_verdict;
                const svColor = sv === 'SHADOW_APPROVED' ? 'text-buy border-buy' :
                                sv === 'SHADOW_KILLED'   ? 'text-hold border-hold' :
                                                            'text-sell border-sell/60';
                const oc = d.outcome;
                const ocColor = oc === 'hit_target' ? 'text-buy' :
                                oc === 'hit_stop'   ? 'text-sell' :
                                oc === 'open'       ? 'text-hold' :
                                                       'text-[var(--txt-muted)]';
                const vetoes = (d.risk_vetoes || []).concat(d.confluence_soft_fails || []);
                return (
                  <tr key={d.id} className="border-b border-[var(--border-default)]" data-testid={`shadow-row-${d.id}`}>
                    <td className="py-2 px-2 text-[10px] text-[var(--txt-muted)]">
                      {new Date(d.created_at).toLocaleTimeString()}
                    </td>
                    <td className="py-2 px-2 font-bold text-cyan-sys">{d.symbol}</td>
                    <td className="py-2 px-2">
                      <span className={`inline-block text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 border ${svColor}`}>
                        {sv === 'SHADOW_APPROVED' ? '✔ approved' : sv === 'SHADOW_KILLED' ? '⚠ killed' : '✖ rejected'}
                      </span>
                    </td>
                    <td className="py-2 px-2 text-right">{hyp.qty || '—'}</td>
                    <td className="py-2 px-2 text-right">{hyp.entry != null ? Number(hyp.entry).toFixed(2) : '—'}</td>
                    <td className="py-2 px-2 text-right text-sell">{hyp.stop != null ? Number(hyp.stop).toFixed(2) : '—'}</td>
                    <td className="py-2 px-2 text-right text-buy">{hyp.target != null ? Number(hyp.target).toFixed(2) : '—'}</td>
                    <td className="py-2 px-2 text-right">{hyp.risk_reward != null ? Number(hyp.risk_reward).toFixed(2) : '—'}</td>
                    <td className="py-2 px-2 text-right">{fmtUSD(hyp.notional)}</td>
                    <td className={`py-2 px-2 text-[10px] uppercase tracking-widest ${ocColor}`}>
                      {oc === 'n_a' ? '—' : oc || '—'}
                      {d.outcome_price != null && <span className="text-[9px] opacity-70 ml-1">@{Number(d.outcome_price).toFixed(2)}</span>}
                    </td>
                    <td className="py-2 px-2 text-[10px] text-[var(--txt-secondary)]" title={vetoes.join(' · ')}>
                      {vetoes.length ? vetoes.slice(0, 2).join(' · ') : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CornerPanel>

      {/* Iter 96 · Group 7 — Position Manager */}
      <CornerPanel
        title={`Position Manager // ${managed.length}`}
        testId="pos-manager-panel"
        right={
          <div className="flex items-center gap-2 text-[10px] font-mono">
            <span className={`uppercase tracking-widest ${posEngine?.running ? 'text-buy' : 'text-sell'}`}
                  data-testid="pos-engine-status">
              {posEngine?.running ? `● live · ${posEngine.cycles} cycles` : '○ offline'}
            </span>
            {posEngine?.last_error && (
              <span className="text-sell" title={posEngine.last_error}>⚠ err</span>
            )}
            <button onClick={forceTick}
                    data-testid="pos-force-tick-btn"
                    className="text-hold hover:text-cyan-sys border border-[var(--border-default)] px-2 py-0.5 uppercase tracking-widest">
              tick now
            </button>
            <button onClick={() => setShowPosCfg((v) => !v)}
                    data-testid="pos-config-toggle-btn"
                    className="text-hold hover:text-cyan-sys inline-flex items-center gap-1 border border-[var(--border-default)] px-2 py-0.5 uppercase tracking-widest">
              <Sliders size={11} /> {showPosCfg ? 'hide' : 'rules'}
            </button>
          </div>
        }
      >
        {showPosCfg && posCfg && (
          <div data-testid="pos-config-editor"
               className="border border-[var(--border-default)] bg-black/40 px-3 py-3 mb-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px] font-mono">
            {[
              ['poll_interval_s',        'Poll interval s',       5,   300],
              ['trail_activate_r',       'Trail activate R',      0.1, 5],
              ['trail_distance_r',       'Trail distance R',      0.1, 3],
              ['scale_out_at_r',         'Scale-out at R',        0.5, 5],
              ['scale_out_pct',          'Scale-out %',           10,  90],
              ['time_stop_bars',         'Time stop bars',        1,   500],
              ['min_r_for_time_bypass',  'Time bypass min R',     0,   3],
            ].map(([k, label, min, max]) => (
              <label key={k} className="flex flex-col gap-1">
                <span className="text-[10px] uppercase tracking-widest text-[var(--txt-muted)]">{label}</span>
                <input
                  type="number"
                  step="0.1"
                  min={min}
                  max={max}
                  data-testid={`pos-cfg-${k}`}
                  value={posCfg[k]}
                  onChange={(e) => setPosCfg((c) => ({ ...c, [k]: Number(e.target.value) }))}
                  onBlur={(e) => {
                    const next = Number(e.target.value);
                    if (next === posCfg[k]) return;
                    updatePosCfg({ [k]: next });
                  }}
                  className="bg-black border border-[var(--border-default)] focus:border-cyan-sys px-2 py-1 text-cyan-sys outline-none"
                />
              </label>
            ))}
            <label className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-widest text-[var(--txt-muted)]">Daily loss lockout</span>
              <button
                data-testid="pos-cfg-daily_pnl_lockout_enabled"
                onClick={() => updatePosCfg({ daily_pnl_lockout_enabled: !posCfg.daily_pnl_lockout_enabled })}
                className={`px-2 py-1 border uppercase tracking-widest ${posCfg.daily_pnl_lockout_enabled ? 'border-buy text-buy' : 'border-sell text-sell'}`}
              >
                {posCfg.daily_pnl_lockout_enabled ? '● armed' : '○ off'}
              </button>
            </label>
          </div>
        )}
        <div className="mobile-scroll" data-sticky-first="true">
          <table className="w-full text-[12px] font-mono min-w-[900px]" data-testid="pos-manager-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-[var(--txt-muted)] border-b border-[var(--border-default)]">
                <th className="text-left  py-2 px-2">symbol</th>
                <th className="text-left  py-2 px-2">side</th>
                <th className="text-right py-2 px-2">qty</th>
                <th className="text-right py-2 px-2">entry</th>
                <th className="text-right py-2 px-2">stop</th>
                <th className="text-right py-2 px-2">last</th>
                <th className="text-right py-2 px-2" title="Current R multiple">R now</th>
                <th className="text-right py-2 px-2" title="Highest R hit while position was open">best R</th>
                <th className="text-center py-2 px-2" title="Scale-out at +1R already executed?">50% out</th>
                <th className="text-right py-2 px-2" title="Poll cycles since open — feeds time-stop rule">bars</th>
                <th className="text-right py-2 px-2">unrealized</th>
              </tr>
            </thead>
            <tbody>
              {managed.length === 0 && (
                <tr><td colSpan={11} className="py-6 text-center text-[var(--txt-muted)]">
                  // no ATDE-managed positions yet — execute an approved signal to open one
                </td></tr>
              )}
              {managed.map((p) => {
                const upl = Number(p.unrealized_pl || 0);
                const rn = Number(p.current_r || 0);
                const rb = Number(p.best_r || 0);
                return (
                  <tr key={p.id} className="border-b border-[var(--border-default)]" data-testid={`pos-managed-${p.symbol}`}>
                    <td className="py-2 px-2 font-bold text-cyan-sys">{p.symbol}</td>
                    <td className="py-2 px-2 text-[10px] uppercase tracking-widest">
                      {p.side === 'long' ? <span className="text-buy inline-flex items-center gap-1"><TrendingUp size={11}/> LONG</span>
                                          : <span className="text-sell inline-flex items-center gap-1"><TrendingDown size={11}/> SHORT</span>}
                    </td>
                    <td className="py-2 px-2 text-right">{p.qty_current}</td>
                    <td className="py-2 px-2 text-right">{Number(p.entry_price).toFixed(2)}</td>
                    <td className="py-2 px-2 text-right text-sell">{p.stop_price != null ? Number(p.stop_price).toFixed(2) : '—'}</td>
                    <td className="py-2 px-2 text-right">{p.last_price != null ? Number(p.last_price).toFixed(2) : '—'}</td>
                    <td className={`py-2 px-2 text-right ${rn > 0 ? 'text-buy' : rn < 0 ? 'text-sell' : ''}`}>{rn.toFixed(2)}</td>
                    <td className="py-2 px-2 text-right text-buy">{rb.toFixed(2)}</td>
                    <td className="py-2 px-2 text-center text-[10px] uppercase tracking-widest">
                      {p.scale_out_done
                        ? <span className="text-buy">✓ done</span>
                        : <span className="text-[var(--txt-muted)]">— pending</span>}
                    </td>
                    <td className="py-2 px-2 text-right">{p.time_bars}</td>
                    <td className={`py-2 px-2 text-right ${upl >= 0 ? 'text-buy' : 'text-sell'}`}>{fmtUSD(upl)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CornerPanel>

      {/* Roadmap tease — Groups 1-8 done */}
      <CornerPanel title="Group 1-8 · Foundation · Watch · Signal · Confluence · Universe · Risk · Positions · Shadow">
        <div className="text-[11px] text-[var(--txt-secondary)] leading-relaxed space-y-1">
          <div className="flex items-center gap-2 text-buy"><Zap size={12} /> <span className="uppercase tracking-widest text-[10px]">operational</span></div>
          <div>· Alpaca broker abstraction — <span className="text-hold font-bold">PAPER ONLY</span> · live-mode disabled</div>
          <div>· Continuous Market Watch + Signal + Confluence + Discovery-driven Universe</div>
          <div>· Risk Engine — 1% sizing · 5 max pos · 3% daily loss cap · 60s cooldown · pre-submit safety trio (kill re-check · fresh buying power · fresh risk assess)</div>
          <div>· Bracket orders with idempotent client_order_id · full bot_orders audit trail</div>
          <div>· Position Engine polling Alpaca every {posCfg?.poll_interval_s ?? 15}s · trailing stops (ratchet-only) · scale-out at +1R (idempotent) · time-stop {posCfg?.time_stop_bars ?? 20} bars · daily P&amp;L → live circuit breaker</div>
          <div>· <span className="text-buy">Group 8 · Phase 15</span> — Shadow Mode: autonomous decision loop, log-only, never submits · 24h stats + outcome tracking for validation</div>
          <div className="pt-2 flex items-center gap-2 text-[var(--txt-muted)]"><span className="uppercase tracking-widest text-[10px]">next</span></div>
          <div>· <span className="text-cyan-sys">Group 8 · Phase 16 (deferred):</span> Limited-Capital PAPER auto-submit once Shadow observations validate behaviour</div>
          <div>· <span className="text-cyan-sys">Group 9:</span> Supervised → Autonomous</div>
        </div>
      </CornerPanel>
    </div>
  );
}

function StatCard({ label, value, color, testId }) {
  return (
    <div className="panel brackets p-3" data-testid={testId}>
      <span className="bk-bl" /><span className="bk-br" />
      <div className="text-[9px] uppercase tracking-widest text-[var(--txt-muted)]">{label}</div>
      <div className={`text-lg font-mono font-bold ${color}`}>{value}</div>
    </div>
  );
}

function MiniStat({ label, value, color, testId }) {
  return (
    <div className="border border-[var(--border-default)] bg-black/40 px-2 py-1.5" data-testid={testId}>
      <div className="text-[9px] uppercase tracking-widest text-[var(--txt-muted)]">{label}</div>
      <div className={`text-[13px] font-mono font-bold ${color || 'text-cyan-sys'}`}>{value}</div>
    </div>
  );
}

const VERDICT_STYLE = {
  INSUFFICIENT_DATA: { color: 'text-[var(--txt-muted)]', border: 'border-[var(--border-default)]', bg: 'bg-black/40',       label: '◌ INSUFFICIENT DATA', hint: 'Need more resolved decisions to judge — this is not statistical proof.' },
  TUNING:            { color: 'text-hold',                border: 'border-hold',                    bg: 'bg-hold/10',       label: '◐ TUNING',            hint: 'Enough data, but one or more metrics need improvement.' },
  PROMISING:         { color: 'text-buy',                 border: 'border-buy',                     bg: 'bg-buy/10',        label: '◑ PROMISING · INITIAL', hint: 'All initial gates pass — informational checkpoint, NOT statistical proof. VALIDATED requires a larger sample.' },
  VALIDATED:         { color: 'text-buy',                 border: 'border-buy',                     bg: 'bg-buy/20',        label: '● VALIDATED',         hint: 'Reserved — locked from these initial thresholds.' },
};

function ShadowValidationCard({ data, window, onWindowChange,
                                 version, onVersionChange,
                                 horizon, onHorizonChange,
                                 availableVersions }) {
  const v = VERDICT_STYLE[data.verdict] || VERDICT_STYLE.INSUFFICIENT_DATA;
  const g = data.gates || {};
  const wins = data.r_metrics || {};
  const outc = data.outcomes || {};
  const counts = data.counts || {};
  const rates = data.rates || {};
  const perSym = Object.entries(data.per_symbol || {}).sort((a,b) => b[1].sum_r - a[1].sum_r).slice(0, 6);
  const perDir = Object.entries(data.per_direction || {});
  const winds  = data.windows || {};
  const vetoes = data.veto_attribution || [];

  const gate = (name, label) => {
    const gg = g[name] || {};
    return (
      <div className="flex items-center justify-between text-[11px]" data-testid={`val-gate-${name}`}>
        <span className="text-[var(--txt-secondary)]">{label}</span>
        <span className={gg.ok ? 'text-buy' : 'text-sell'}>
          {gg.ok ? '✓' : '✗'} <span className="text-[var(--txt-muted)] ml-1">{typeof gg.actual === 'number' ? gg.actual : gg.actual}</span>
        </span>
      </div>
    );
  };

  return (
    <div className={`border-2 ${v.border} ${v.bg} px-3 py-3 mb-3 space-y-3`} data-testid="shadow-validation-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className={`font-bold tracking-[0.3em] uppercase text-[12px] ${v.color}`} data-testid="val-verdict-pill">
            {v.label}
          </div>
          <div className="text-[10px] text-[var(--txt-secondary)] mt-0.5">{v.hint}</div>
        </div>
        <div className="flex items-center gap-1 text-[10px] font-mono">
          <span className="text-[var(--txt-muted)] uppercase tracking-widest mr-1">window</span>
          {[6, 24, 168].map((h) => (
            <button key={h} onClick={() => onWindowChange(h)}
                    data-testid={`val-window-${h}h`}
                    className={`px-2 py-1 border uppercase tracking-widest ${window === h ? 'border-cyan-sys text-cyan-sys' : 'border-[var(--border-default)] text-[var(--txt-secondary)] hover:text-cyan-sys'}`}>
              {h === 168 ? '7d' : `${h}h`}
            </button>
          ))}
        </div>
      </div>

      {/* Iter 99 — Version + horizon selectors */}
      <div className="flex flex-wrap items-center gap-2 border-t border-[var(--border-default)] pt-2 text-[10px] font-mono" data-testid="val-selectors">
        <span className="text-[var(--txt-muted)] uppercase tracking-widest mr-1">version</span>
        {['v1_pre_tuning', 'v2_tuned'].map((ver) => {
          const n = availableVersions?.distribution?.[ver] || 0;
          return (
            <button key={ver} onClick={() => onVersionChange(ver)}
                    data-testid={`val-version-${ver}`}
                    className={`px-2 py-1 border uppercase tracking-widest ${version === ver ? 'border-cyan-sys text-cyan-sys' : 'border-[var(--border-default)] text-[var(--txt-secondary)] hover:text-cyan-sys'}`}>
              {ver.replace('_', ' ')} <span className="text-[var(--txt-muted)]">·{n}</span>
            </button>
          );
        })}
        <span className="text-[var(--txt-muted)] uppercase tracking-widest ml-3 mr-1">horizon</span>
        {['20m', '1h', '4h', '1d'].map((h) => (
          <button key={h} onClick={() => onHorizonChange(h)}
                  data-testid={`val-horizon-${h}`}
                  className={`px-2 py-1 border uppercase tracking-widest ${horizon === h ? 'border-hold text-hold' : 'border-[var(--border-default)] text-[var(--txt-secondary)] hover:text-hold'}`}>
            {h}
          </button>
        ))}
        <span className="text-[var(--txt-secondary)] ml-3 italic">
          approval-rate range 15–45% is a DIAGNOSTIC benchmark — not an optimization target
        </span>
      </div>

      {(data.reasons || []).length > 0 && (
        <div className="text-[11px] font-mono text-[var(--txt-secondary)]" data-testid="val-reasons">
          {data.reasons.map((r, i) => <div key={i}>· {r}</div>)}
        </div>
      )}

      {/* Gate summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 border-t border-[var(--border-default)] pt-3" data-testid="val-gates">
        {gate('sample_size',   `Resolved ≥ ${g.sample_size?.min}`)}
        {gate('approval_rate', `Approval ${g.approval_rate?.range?.[0]}–${g.approval_rate?.range?.[1]}%`)}
        {gate('hit_rate',      `Hit rate ≥ ${g.hit_rate?.min}%`)}
        {gate('expectancy_r',  `Expectancy R > 0`)}
      </div>

      {/* Metrics grid */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-[11px] font-mono border-t border-[var(--border-default)] pt-3" data-testid="val-metrics">
        <MiniStat testId="val-total"        label="total decisions" value={counts.total_decisions} />
        <MiniStat testId="val-resolved"     label="resolved"         value={counts.resolved}         color="text-buy" />
        <MiniStat testId="val-unresolved"   label="unresolved"       value={counts.unresolved}       color="text-hold" />
        <MiniStat testId="val-approval"     label="approval rate"    value={`${rates.approval_rate_pct}%`} />
        <MiniStat testId="val-win-rate"     label="win rate"         value={`${rates.win_rate_pct}%`} color="text-buy" />
        <MiniStat testId="val-loss-rate"    label="loss rate"        value={`${rates.loss_rate_pct}%`} color="text-sell" />
        <MiniStat testId="val-avg-win"      label="avg win R"        value={wins.avg_win_r?.toFixed?.(2) ?? '—'} color="text-buy" />
        <MiniStat testId="val-avg-loss"     label="avg loss R"       value={wins.avg_loss_r?.toFixed?.(2) ?? '—'} color="text-sell" />
        <MiniStat testId="val-expectancy"   label="expectancy R"     value={wins.expectancy_r?.toFixed?.(3) ?? '—'} color={wins.expectancy_r > 0 ? 'text-buy' : 'text-sell'} />
        <MiniStat testId="val-profit-factor" label="profit factor"   value={wins.profit_factor != null ? wins.profit_factor.toFixed(2) : '—'} />
        <MiniStat testId="val-max-win"      label="max win streak"   value={data.streaks?.max_win_streak}  color="text-buy" />
        <MiniStat testId="val-max-loss"     label="max loss streak"  value={data.streaks?.max_loss_streak} color="text-sell" />
      </div>

      {/* Rolling windows */}
      <div className="grid grid-cols-3 gap-2 text-[11px] font-mono border-t border-[var(--border-default)] pt-3" data-testid="val-rolling">
        {['6h', '24h', '7d'].map((k) => {
          const wnd = winds[k] || {};
          return (
            <div key={k} className="border border-[var(--border-default)] bg-black/40 px-2 py-1.5" data-testid={`val-window-${k}`}>
              <div className="text-[9px] uppercase tracking-widest text-[var(--txt-muted)]">{k} · total {wnd.total} · resolved {wnd.resolved}</div>
              <div className="text-[13px] flex gap-3">
                <span>win <span className="text-buy">{wnd.win_rate_pct}%</span></span>
                <span>exp <span className={wnd.expectancy_r > 0 ? 'text-buy' : 'text-sell'}>{wnd.expectancy_r}</span></span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Outcome distribution + veto attribution */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[11px] font-mono border-t border-[var(--border-default)] pt-3">
        <div data-testid="val-outcomes">
          <div className="text-[10px] uppercase tracking-widest text-[var(--txt-muted)] mb-1">Outcome distribution</div>
          <div className="flex gap-4">
            <span>✓ target <span className="text-buy">{outc.hit_target}</span></span>
            <span>✗ stop <span className="text-sell">{outc.hit_stop}</span></span>
            <span>· flat <span className="text-[var(--txt-secondary)]">{outc.window_flat}</span></span>
            <span>◌ open <span className="text-hold">{outc.open}</span></span>
          </div>
        </div>
        <div data-testid="val-veto-attribution">
          <div className="text-[10px] uppercase tracking-widest text-[var(--txt-muted)] mb-1">Vetoes by layer (top {Math.min(8, vetoes.length)})</div>
          <div className="flex flex-wrap gap-x-3 gap-y-1">
            {vetoes.slice(0, 8).map((v, i) => (
              <span key={i}>
                <span className="text-sell">{v.layer}</span> <span className="opacity-70">×{v.count}</span>
              </span>
            ))}
            {vetoes.length === 0 && <span className="text-[var(--txt-muted)]">— none —</span>}
          </div>
        </div>
      </div>

      {/* Per-symbol breakdown (top 6 by sum R) */}
      {perSym.length > 0 && (
        <div className="border-t border-[var(--border-default)] pt-3" data-testid="val-per-symbol">
          <div className="text-[10px] uppercase tracking-widest text-[var(--txt-muted)] mb-1">Top symbols by cumulative R</div>
          <div className="mobile-scroll">
            <table className="w-full text-[11px] font-mono min-w-[520px]">
              <thead className="text-[9px] uppercase tracking-widest text-[var(--txt-muted)] border-b border-[var(--border-default)]">
                <tr>
                  <th className="text-left py-1 px-2">symbol</th>
                  <th className="text-right py-1 px-2">n</th>
                  <th className="text-right py-1 px-2">wins</th>
                  <th className="text-right py-1 px-2">losses</th>
                  <th className="text-right py-1 px-2">win %</th>
                  <th className="text-right py-1 px-2">avg win R</th>
                  <th className="text-right py-1 px-2">avg loss R</th>
                  <th className="text-right py-1 px-2">sum R</th>
                </tr>
              </thead>
              <tbody>
                {perSym.map(([sym, s]) => (
                  <tr key={sym} className="border-b border-[var(--border-default)]/50">
                    <td className="py-1 px-2 text-cyan-sys">{sym}</td>
                    <td className="py-1 px-2 text-right">{s.n}</td>
                    <td className="py-1 px-2 text-right text-buy">{s.wins}</td>
                    <td className="py-1 px-2 text-right text-sell">{s.losses}</td>
                    <td className="py-1 px-2 text-right">{s.win_rate_pct}%</td>
                    <td className="py-1 px-2 text-right text-buy">{s.avg_win_r}</td>
                    <td className="py-1 px-2 text-right text-sell">{s.avg_loss_r}</td>
                    <td className={`py-1 px-2 text-right ${s.sum_r > 0 ? 'text-buy' : s.sum_r < 0 ? 'text-sell' : ''}`}>{s.sum_r}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Per-direction summary */}
      {perDir.length > 0 && (
        <div className="border-t border-[var(--border-default)] pt-3 text-[11px] font-mono" data-testid="val-per-direction">
          <div className="text-[10px] uppercase tracking-widest text-[var(--txt-muted)] mb-1">Per direction (proxy for regime)</div>
          <div className="flex flex-wrap gap-4">
            {perDir.map(([d, s]) => (
              <span key={d}>
                <span className="uppercase text-cyan-sys tracking-widest">{d}</span>
                <span className="ml-2 text-[var(--txt-secondary)]">n={s.n} · win {s.win_rate_pct}% · sumR {s.sum_r}</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Iter 100 — Shadow Replay Lab (READ-ONLY COUNTERFACTUAL — NOT A BACKTEST)
const REPLAY_GATE_FIELDS = [
  ['min_score',              'min score',           0,   100],
  ['min_confluence',         'min confluence',      0,   100],
  ['min_rr',                 'min R:R',             0.5, 10],
  ['regime_confidence_min',  'min regime conf',     0,   1],
  ['min_vol_ratio',          'min vol ratio',       0,   5],
  ['max_atr_pct',            'max ATR %',           0.5, 20],
];

function ShadowReplayLab({ gates, onGatesChange, onRun, onLoadPreset, running, result, presets }) {
  const [discoveryText, setDiscoveryText] = React.useState(
    (gates.discovery_ok || []).join(','));
  React.useEffect(() => { setDiscoveryText((gates.discovery_ok || []).join(',')); },
                   [gates.discovery_ok]);

  return (
    <div className="border-2 border-cyan-sys/40 bg-black/40 px-3 py-3 mb-3 space-y-3"
         data-testid="shadow-replay-lab">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="font-bold tracking-[0.3em] uppercase text-[12px] text-cyan-sys" data-testid="replay-label">
            ◫ COUNTERFACTUAL REPLAY — NOT A BACKTEST
          </div>
          <div className="text-[10px] text-[var(--txt-secondary)] mt-0.5">
            Re-filters historical Shadow decisions against alternative gates. Read-only. Cannot promote strategy or unlock live/limited modes.
          </div>
        </div>
        <button onClick={onRun} disabled={running}
                data-testid="replay-run-btn"
                className="border border-cyan-sys text-cyan-sys hover:bg-cyan-sys hover:text-black px-3 py-1.5 text-[11px] uppercase tracking-widest disabled:opacity-50">
          {running ? '· running…' : '▶ run replay'}
        </button>
      </div>

      {/* Preset shortcuts */}
      {presets?.presets && (
        <div className="flex flex-wrap items-center gap-2 text-[10px] font-mono" data-testid="replay-presets">
          <span className="text-[var(--txt-muted)] uppercase tracking-widest">preset</span>
          {Object.entries(presets.presets).map(([k, p]) => (
            <button key={k} onClick={() => onLoadPreset(k)}
                    data-testid={`replay-preset-${k}`}
                    className="border border-[var(--border-default)] text-[var(--txt-secondary)] hover:border-cyan-sys hover:text-cyan-sys px-2 py-1 uppercase tracking-widest"
                    title={p.description}>
              {p.label}
            </button>
          ))}
        </div>
      )}

      {/* Gate editor */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px] font-mono border-t border-[var(--border-default)] pt-3" data-testid="replay-gate-editor">
        {REPLAY_GATE_FIELDS.map(([k, label, min, max]) => (
          <label key={k} className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-widest text-[var(--txt-muted)]">{label}</span>
            <input
              type="number" step="0.1" min={min} max={max}
              data-testid={`replay-gate-${k}`}
              value={gates[k] ?? ''}
              onChange={(e) => {
                const v = e.target.value === '' ? undefined : Number(e.target.value);
                onGatesChange((g) => {
                  const next = { ...g };
                  if (v === undefined) delete next[k]; else next[k] = v;
                  return next;
                });
              }}
              className="bg-black border border-[var(--border-default)] focus:border-cyan-sys px-2 py-1 text-cyan-sys outline-none"
            />
          </label>
        ))}
        <label className="flex flex-col gap-1 col-span-2">
          <span className="text-[10px] uppercase tracking-widest text-[var(--txt-muted)]">discovery_ok (comma-separated)</span>
          <input
            type="text"
            data-testid="replay-gate-discovery_ok"
            value={discoveryText}
            onChange={(e) => setDiscoveryText(e.target.value)}
            onBlur={() => {
              const arr = discoveryText.split(',').map((s) => s.trim()).filter(Boolean);
              onGatesChange((g) => ({ ...g, discovery_ok: arr.length ? arr : undefined }));
            }}
            placeholder="A+,A,B"
            className="bg-black border border-[var(--border-default)] focus:border-cyan-sys px-2 py-1 text-cyan-sys outline-none"
          />
        </label>
      </div>

      {/* Result panel */}
      {result && (
        <div className="border-t border-[var(--border-default)] pt-3 space-y-3" data-testid="replay-result">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px] font-mono">
            <MiniStat testId="replay-original-approved" label="original approved" value={result.counts.original_approved} />
            <MiniStat testId="replay-ctf-approved"      label="counterfactual approved" value={result.counts.counterfactual_approved} color="text-buy" />
            <MiniStat testId="replay-ctf-rejected"      label="counterfactual rejected" value={result.counts.counterfactual_rejected} color="text-sell" />
            <MiniStat testId="replay-missing-ctx"       label="excluded (missing ctx)" value={result.counts.excluded_missing_context} color="text-hold" />
          </div>
          <div className="text-[11px] font-mono">
            Approval-rate delta: <span className={result.approval_rate_delta_pct >= 0 ? 'text-buy' : 'text-sell'}>
              {result.approval_rate_delta_pct >= 0 ? '+' : ''}{result.approval_rate_delta_pct}%
            </span>
          </div>

          {/* Side-by-side metric grid */}
          <div className="grid grid-cols-2 gap-3 border-t border-[var(--border-default)] pt-3">
            <ReplayColumn testId="replay-live" title="LIVE (v2_tuned original)" data={result.original_validation} />
            <ReplayColumn testId="replay-ctf" title="COUNTERFACTUAL (replay)" data={result.counterfactual_validation} accent />
          </div>

          {/* Rejection attribution */}
          {result.rejection_attribution?.length > 0 && (
            <div className="text-[10px] font-mono border-t border-[var(--border-default)] pt-3" data-testid="replay-rejections">
              <div className="uppercase tracking-widest text-[var(--txt-muted)] mb-1">Rejection attribution</div>
              <div className="flex flex-wrap gap-x-3 gap-y-1">
                {result.rejection_attribution.slice(0, 12).map((r, i) => (
                  <span key={i}>
                    <span className={r.gate.startsWith('missing::') ? 'text-hold' : 'text-sell'}>{r.gate}</span>
                    <span className="opacity-70"> ×{r.count}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="text-[10px] italic text-[var(--txt-muted)] border-t border-[var(--border-default)] pt-2">
            ⓘ {result.warning}
          </div>
        </div>
      )}
    </div>
  );
}

function ReplayColumn({ testId, title, data, accent }) {
  const r = data?.r_metrics || {};
  const rt = data?.rates || {};
  const c = data?.counts || {};
  return (
    <div className={`border ${accent ? 'border-cyan-sys/60' : 'border-[var(--border-default)]'} p-2`} data-testid={testId}>
      <div className={`text-[10px] uppercase tracking-widest ${accent ? 'text-cyan-sys' : 'text-[var(--txt-muted)]'} mb-1`}>{title}</div>
      <div className="text-[11px] font-mono space-y-0.5">
        <div>verdict: <span className={accent ? 'text-cyan-sys' : 'text-[var(--txt-secondary)]'}>{data?.verdict || '—'}</span></div>
        <div>approved / resolved: {c.approved} / {c.resolved}</div>
        <div>approval rate: {rt.approval_rate_pct}%</div>
        <div>win rate: <span className="text-buy">{rt.win_rate_pct}%</span></div>
        <div>expectancy R: <span className={r.expectancy_r > 0 ? 'text-buy' : 'text-sell'}>{r.expectancy_r?.toFixed?.(3) ?? '—'}</span></div>
        <div>profit factor: {r.profit_factor != null ? r.profit_factor.toFixed(2) : '—'}</div>
        <div>avg win / loss R: <span className="text-buy">{r.avg_win_r}</span> / <span className="text-sell">{r.avg_loss_r}</span></div>
      </div>
    </div>
  );
}
